import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExpenseDialog({ open, onClose, onSuccess, eventId, expense = null }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    category: 'gas',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });

  // Reset form when dialog opens or expense changes
  useEffect(() => {
    if (expense) {
      setFormData({
        category: expense.category || 'gas',
        amount: expense.amount?.toString() || '',
        description: expense.description || '',
        date: expense.date || new Date().toISOString().split('T')[0],
      });
    } else {
      setFormData({
        category: 'gas',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
      });
    }
  }, [expense, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = {
        event_id: eventId,
        category: formData.category,
        amount: parseFloat(formData.amount),
        description: formData.description,
        date: formData.date,
      };

      if (expense) {
        // Update existing expense
        await axios.put(`${BACKEND_URL}/api/expenses/${expense.id}`, payload, { headers });
        toast.success('Expense updated successfully');
      } else {
        // Create new expense
        await axios.post(`${BACKEND_URL}/api/expenses`, payload, { headers });
        toast.success('Expense added successfully');
      }

      setFormData({
        category: 'gas',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">{expense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="category">Category *</Label>
            <select
              id="category"
              name="category"
              data-testid="expense-category-select"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary h-11"
            >
              <option value="gas">Gas</option>
              <option value="hotel">Hotel</option>
              <option value="flight">Flight</option>
              <option value="uhaul_towing">Uhaul/Towing</option>
              <option value="employee_wages">Employee Wages</option>
              <option value="power">Power/Electric</option>
              <option value="food">Food</option>
              <option value="supplies">Supplies</option>
              <option value="misc">Miscellaneous</option>
            </select>
          </div>

          <div>
            <Label htmlFor="amount">Amount *</Label>
            <Input
              id="amount"
              name="amount"
              type="number"
              step="0.01"
              data-testid="expense-amount-input"
              value={formData.amount}
              onChange={handleChange}
              required
              className="bg-secondary/50 border-transparent focus:border-primary"
            />
          </div>

          <div>
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              name="date"
              type="date"
              data-testid="expense-date-input"
              value={formData.date}
              onChange={handleChange}
              required
              className="bg-secondary/50 border-transparent focus:border-primary"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              data-testid="expense-description-input"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="bg-secondary/50 border-transparent focus:border-primary"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="expense-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="expense-submit-button"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? 'Saving...' : expense ? 'Update Expense' : 'Add Expense'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
